import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export function Modal({
  titulo,
  subtitulo,
  onClose,
  children,
  acciones,
}: {
  titulo: string;
  subtitulo?: string;
  onClose: () => void;
  children?: ReactNode;
  acciones: ReactNode;
}) {
  useEffect(() => {
    const cerrarConEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', cerrarConEsc);
    return () => window.removeEventListener('keydown', cerrarConEsc);
  }, [onClose]);

  return createPortal(
    <div className="overlay" onClick={onClose} role="presentation">
      <div
        className="dialogo"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
      >
        <header className="dialogo-encabezado">
          <h2>{titulo}</h2>
          {subtitulo && <p className="dialogo-sub">{subtitulo}</p>}
        </header>
        {children && <div className="dialogo-cuerpo">{children}</div>}
        <footer className="dialogo-botones">{acciones}</footer>
      </div>
    </div>,
    document.body,
  );
}

/** Confirmación con el mismo lenguaje visual que el resto, en vez de window.confirm. */
export function Confirmar({
  titulo,
  mensaje,
  etiquetaAceptar = 'Aceptar',
  peligro = false,
  onCancelar,
  onAceptar,
}: {
  titulo: string;
  mensaje: string;
  etiquetaAceptar?: string;
  peligro?: boolean;
  onCancelar: () => void;
  onAceptar: () => void;
}) {
  const [enviando, setEnviando] = useState(false);

  return (
    <Modal
      titulo={titulo}
      onClose={onCancelar}
      acciones={
        <>
          <button className="secundario" onClick={onCancelar}>
            Cancelar
          </button>
          <button
            className={peligro ? 'peligro-solido' : ''}
            disabled={enviando}
            onClick={() => {
              setEnviando(true);
              onAceptar();
            }}
          >
            {enviando ? 'Un momento…' : etiquetaAceptar}
          </button>
        </>
      }
    >
      <p className="confirmar-mensaje">{mensaje}</p>
    </Modal>
  );
}

/** Pide un texto. Reemplaza a window.prompt. */
export function Pedir({
  titulo,
  etiqueta,
  valorInicial = '',
  ayuda,
  etiquetaAceptar = 'Guardar',
  onCancelar,
  onAceptar,
}: {
  titulo: string;
  etiqueta: string;
  valorInicial?: string;
  ayuda?: string;
  etiquetaAceptar?: string;
  onCancelar: () => void;
  onAceptar: (valor: string) => void;
}) {
  const [valor, setValor] = useState(valorInicial);

  function aceptar() {
    if (valor.trim()) onAceptar(valor.trim());
  }

  return (
    <Modal
      titulo={titulo}
      onClose={onCancelar}
      acciones={
        <>
          <button className="secundario" onClick={onCancelar}>
            Cancelar
          </button>
          <button onClick={aceptar} disabled={!valor.trim()}>
            {etiquetaAceptar}
          </button>
        </>
      }
    >
      <Campo id="pedir-valor" etiqueta={etiqueta} ayuda={ayuda}>
        <input
          id="pedir-valor"
          value={valor}
          autoFocus
          onChange={(e) => setValor(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && aceptar()}
        />
      </Campo>
    </Modal>
  );
}

export type OpcionMenu = {
  etiqueta: string;
  onSelect: () => void;
  peligro?: boolean;
  oculto?: boolean;
};

const ANCHO_MENU = 200;

/**
 * Menú contextual. La lista se dibuja en un portal con posición fija porque las
 * tablas tienen overflow (para las esquinas redondeadas y el scroll horizontal)
 * y eso recortaba el desplegable dejándolo inutilizable.
 */
export function MenuAcciones({ opciones, etiqueta }: { opciones: OpcionMenu[]; etiqueta: string }) {
  const [abierto, setAbierto] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const disparador = useRef<HTMLButtonElement>(null);
  const lista = useRef<HTMLDivElement>(null);

  const visibles = opciones.filter((o) => !o.oculto);

  useLayoutEffect(() => {
    if (!abierto || !disparador.current) return;

    const r = disparador.current.getBoundingClientRect();
    const alto = visibles.length * 33 + 8;

    // Si no entra para abajo, se abre hacia arriba; y nunca se sale por los lados.
    const hayLugarAbajo = window.innerHeight - r.bottom > alto + 8;
    setPos({
      top: hayLugarAbajo ? r.bottom + 4 : Math.max(8, r.top - alto - 4),
      left: Math.min(Math.max(8, r.right - ANCHO_MENU), window.innerWidth - ANCHO_MENU - 8),
    });
  }, [abierto, visibles.length]);

  useEffect(() => {
    if (!abierto) return;

    const alHacerClic = (e: MouseEvent) => {
      if (
        !disparador.current?.contains(e.target as Node) &&
        !lista.current?.contains(e.target as Node)
      ) {
        setAbierto(false);
      }
    };
    const alTeclear = (e: KeyboardEvent) => e.key === 'Escape' && setAbierto(false);
    const cerrar = () => setAbierto(false);

    document.addEventListener('mousedown', alHacerClic);
    document.addEventListener('keydown', alTeclear);
    // Con posición fija, al hacer scroll la lista quedaría flotando lejos del botón.
    window.addEventListener('scroll', cerrar, true);
    window.addEventListener('resize', cerrar);

    return () => {
      document.removeEventListener('mousedown', alHacerClic);
      document.removeEventListener('keydown', alTeclear);
      window.removeEventListener('scroll', cerrar, true);
      window.removeEventListener('resize', cerrar);
    };
  }, [abierto]);

  return (
    <>
      <button
        ref={disparador}
        className="menu-disparador"
        onClick={() => setAbierto((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={abierto}
        aria-label={etiqueta}
      >
        <svg viewBox="0 0 16 16" aria-hidden="true" className="icono-puntos">
          <circle cx="8" cy="3" r="1.5" />
          <circle cx="8" cy="8" r="1.5" />
          <circle cx="8" cy="13" r="1.5" />
        </svg>
      </button>

      {abierto &&
        createPortal(
          <div
            ref={lista}
            className="menu-lista"
            role="menu"
            style={{ top: pos.top, left: pos.left, width: ANCHO_MENU }}
          >
            {visibles.map((o) => (
              <button
                key={o.etiqueta}
                role="menuitem"
                className={o.peligro ? 'menu-item peligro' : 'menu-item'}
                onClick={() => {
                  setAbierto(false);
                  o.onSelect();
                }}
              >
                {o.etiqueta}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}

export function Campo({
  id,
  etiqueta,
  children,
  ayuda,
}: {
  id: string;
  etiqueta: string;
  children: ReactNode;
  ayuda?: string;
}) {
  return (
    <div className="campo">
      <label htmlFor={id}>{etiqueta}</label>
      {children}
      {ayuda && <small className="ayuda">{ayuda}</small>}
    </div>
  );
}
